import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Briefcase, RefreshCw } from "lucide-react";
import Sparkline from "./Sparkline";
import { cn } from "@/lib/utils";

/**
 * PartnerDashboardMock — small stylized visual for the "For Funding Partners" teaser.
 * Shows 3 mini-tiles: Deal Flow (sparkline), Portfolio (donut-ish bars), Repayments (bar chart).
 */
export const PartnerDashboardMock = ({ className }) => {
  const dealFlow = [8, 14, 12, 22, 26, 32, 30, 38, 44, 52];
  const portfolio = [
    { label: "Working Capital", pct: 46 },
    { label: "Growth Line", pct: 32 },
    { label: "Inventory", pct: 22 },
  ];
  const repayments = [62, 74, 68, 80, 88, 91, 86, 94];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        "relative w-full max-w-md rounded-3xl border border-white/[0.06] bg-bg-card p-6 shadow-2xl",
        className,
      )}
      data-testid="partner-dashboard-mock"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/[0.05] pb-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-emerald/12">
            <Briefcase className="h-4 w-4 text-brand-emerald" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-xs font-semibold text-white">
              Partner Console
            </span>
            <span className="text-[10px] text-text-muted">
              Underwriting overview
            </span>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-brand-emerald">
          <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-brand-emerald" />
          Live
        </span>
      </div>

      {/* Grid */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        {/* Deal flow */}
        <div className="col-span-2 rounded-xl border border-white/[0.05] bg-white/[0.02] p-4">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-text-muted">
              Deal Flow
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] text-brand-emerald">
              <TrendingUp className="h-3 w-3" />
              Upward trend
            </span>
          </div>
          <Sparkline
            data={dealFlow}
            width={280}
            height={44}
            stroke="#00E676"
            className="mt-2"
          />
        </div>

        {/* Portfolio */}
        <div className="rounded-xl border border-white/[0.05] bg-white/[0.02] p-4">
          <span className="text-[10px] uppercase tracking-widest text-text-muted">
            Portfolio Mix
          </span>
          <ul className="mt-3 flex flex-col gap-2.5">
            {portfolio.map((p, i) => (
              <li key={p.label} className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-white/80">{p.label}</span>
                  <span className="font-mono text-[10px] text-text-muted">
                    {p.pct}%
                  </span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-white/[0.04]">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${p.pct}%` }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 1.1, delay: 0.15 * i }}
                    className="h-full rounded-full"
                    style={{
                      background: `linear-gradient(90deg, #00C853 0%, #00E676 100%)`,
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Repayments */}
        <div className="rounded-xl border border-white/[0.05] bg-white/[0.02] p-4">
          <div className="flex items-center gap-1.5">
            <RefreshCw className="h-3 w-3 text-brand-emerald" />
            <span className="text-[10px] uppercase tracking-widest text-text-muted">
              Repayment Cadence
            </span>
          </div>
          <div className="mt-3 flex h-14 items-end gap-1">
            {repayments.map((v, i) => (
              <motion.span
                key={i}
                initial={{ height: 0 }}
                whileInView={{ height: `${v}%` }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.6,
                  delay: 0.05 * i,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="w-full rounded-t bg-brand-emerald/60"
                style={{
                  boxShadow: "0 0 8px rgba(0,230,118,0.35)",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PartnerDashboardMock;
